import { Entity, FeedData, Matrix } from './Feed.types';

const getPriority = () => {
    let flag = 1;

    return function () {
        if (flag) {
            flag = 0;

            return Entity.Prompt;
        }
        flag = 1;

        return Entity.Challenge;
    };
};

const getMatrix = (columns: number, priority: () => string, hasCollections: boolean): Matrix => {
    const matrix: Matrix = {
        '0': {
            prompts: columns,
            weight: columns === 2 ? columns * 2 : columns,
        },
        '1': {
            prompts: columns === 6 ? columns - 3 : columns - 2,
            challenge: 1,
            priority: priority(),
            weight: columns,
        },
        '2': {
            prompts: columns,
            weight: columns === 2 ? columns * 2 : columns,
        },
        '3': {
            collections: hasCollections ? 1 : 0,
            courses: hasCollections ? 0 : 1,
            weight: columns,
        },
    };

    return matrix;
};

export const getOrderedFeed = (feedData: FeedData, columns: number) => {
    const { collections, prompts, challenges, courses } = feedData;
    //eslint-disable-next-line @typescript-eslint/no-explicit-any
    const feed: any[] = [];
    // Для одной колонки используем эффективные колонки (2) для расчета весов
    const effectiveColumns = Math.max(columns, 2);
    const weights = {
        prompt: 1,
        challenge: Math.max(Math.floor(effectiveColumns / 2), 2),
        collection: effectiveColumns,
        course: effectiveColumns,
    };
    let collectionIndex = 0;
    let promtIndex = 0;
    let challengeIndex = 0;
    let courseIndex = 0;

    const hasPromptsLeft = () => {
        return promtIndex < prompts.length;
    };

    const canAddChallenge = () => {
        return challengeIndex < challenges.length;
    };

    const canAddCollection = () => {
        return collectionIndex < collections.length;
    };

    const canAddCourse = () => {
        return courseIndex < courses.length;
    };

    const addPrompts = (count: number = 1): number => {
        let added = 0;
        for (let i = 0; i < count && hasPromptsLeft(); i++) {
            feed.push(prompts[promtIndex]);
            promtIndex++;
            added++;
        }

        return added;
    };

    const addChallenge = (): number => {
        if (!canAddChallenge()) {
            return 0;
        }

        feed.push(challenges[challengeIndex]);
        challengeIndex++;

        return weights.challenge;
    };

    const addCollection = (): number => {
        if (!canAddCollection()) {
            return 0;
        }

        feed.push(collections[collectionIndex]);
        collectionIndex++;

        return weights.collection;
    };

    const addCourse = (): number => {
        if (!canAddCourse()) {
            return 0;
        }

        feed.push(courses[courseIndex]);
        courseIndex++;

        return weights.course;
    };

    const priority = getPriority();
    let firstCollectionAdded = !canAddCollection();

    while (hasPromptsLeft()) {
        const matrix = getMatrix(effectiveColumns, priority, canAddCollection());
        const rowsNumber = Object.keys(matrix);

        for (let i = 0; i < rowsNumber.length; i++) {
            const row = matrix[i];
            let rowCurrentWeight = 0;

            if (row.prompts && row.priority === Entity.Prompt) {
                rowCurrentWeight += addPrompts(row.prompts);
            }

            if (
                (hasPromptsLeft() && row.challenge) ||
                (row.challenge && (rowCurrentWeight === 0 || rowCurrentWeight === row.weight - weights.challenge))
            ) {
                rowCurrentWeight += addChallenge();
            }

            // Добавляем подборки или курсы
            if ((hasPromptsLeft() && row.collections) || (row.collections && rowCurrentWeight === 0)) {
                rowCurrentWeight += addCollection();
            } else if ((hasPromptsLeft() && row.courses) || (row.courses && rowCurrentWeight === 0)) {
                rowCurrentWeight += addCourse();
            }

            if (rowCurrentWeight < row.weight) {
                const added = addPrompts(row.weight - rowCurrentWeight);

                rowCurrentWeight += added;

                // Выходим если промпты закончились:
                // - в первой строке (row 0) до вставки первой подборки — только если строка не заполнена
                // - в любой другой строке или после вставки первой подборки — всегда
                const isFirstRowBeforeCollection = !firstCollectionAdded && i === 0;
                const rowNotFilled = rowCurrentWeight < row.weight;

                if (!hasPromptsLeft() && (!isFirstRowBeforeCollection || rowNotFilled)) {
                    break;
                }
            }

            // Вставляем первую подборку после первой строки матрицы (row 0) для всех размеров экрана.
            // Подборка вставляется только если строка была полностью заполнена промптами.
            if (!firstCollectionAdded && i === 0) {
                firstCollectionAdded = true;
                addCollection();
            }

            if (!hasPromptsLeft()) {
                break;
            }
        }
    }

    return feed;
};

const getFeedData = (feedData: FeedData, columns: number) => {
    const { collections, prompts, challenges, courses } = feedData;

    const isOneOfEntities = [collections, prompts, challenges, courses].filter(arr => arr.length).length === 1;

    // если существвуют только один тип сущности, то возвращаем его, иначе возвращаем упорядоченный список
    if (isOneOfEntities) {
        return [...collections, ...prompts, ...challenges, ...courses];
    }

    return getOrderedFeed(feedData, columns);
};

export const useFeed = (feedData: FeedData, columns: number) => {
    const feed = getFeedData(feedData, columns);

    return feed;
};
